import { Link } from 'react-router-dom';
import { MatchupMarks, duotoneBand } from './TeamMark';
import { PageBanner } from './PageBanner';
import { isFinal, isLive, kickoff, type Game } from '../lib/api';
import { logo } from '../lib/logo';

/**
 * Just the stadium.
 *
 * ESPN disambiguates the several Memorial Stadiums by appending the city, so one
 * card read "Davis Wade Stadium" and the next "Memorial Stadium (Clemson, SC)".
 * The city is already on the record separately when it is wanted.
 */
const stadium = (venue: string) => venue.replace(/\s*\([^)]*\)\s*$/, '');

/**
 * One game across the top of a page, in both clubs' colours with their marks
 * bleeding off the edges. Used by the home carousel and by each schedule.
 */
export function GameBanner({ g }: { g: Game }) {
  const k = kickoff(g.date);
  const started = isLive(g) || isFinal(g);
  return (
    <PageBanner fill="none" style={duotoneBand(g.away_team, g.home_team)}
                marks={<MatchupMarks away={g.away_team} home={g.home_team} />}>
      <div>
        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="pill bg-paper/20 text-paper">{g.league === 'cfb' ? 'College' : 'NFL'}</span>
          {isLive(g)
            ? <span className="pill bg-red text-paper">{g.status_detail}</span>
            : <span className="meta !text-paper/90">{isFinal(g) ? 'Final' : `${k.day}  ${k.time}`}</span>}
          {g.venue && <span className="meta !text-paper/70">{stadium(g.venue)}</span>}
        </div>

        <div className="flex flex-col gap-5 md:flex-row md:flex-wrap md:items-end md:justify-between md:gap-6">
          <div className="min-w-0 flex-1">
            {/* phones stack the two clubs like a score block; wide screens read across */}
            <div className="flex flex-col gap-2 md:hidden">
              {[g.away_team, g.home_team].map((t) => (
                <div key={t.abbreviation} className="flex items-center gap-3">
                  {t.logo && (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm bg-white/90 p-1.5">
                      <img src={logo(t.logo, 80)} alt="" fetchPriority="high" decoding="async" className="h-full w-full object-contain" />
                    </span>
                  )}
                  <span className="display min-w-0 flex-1 truncate text-[38px] leading-none">{t.abbreviation}</span>
                  {started && <span className="score text-[34px] leading-none">{t.score ?? 0}</span>}
                </div>
              ))}
            </div>

            <div className="hidden items-center gap-4 md:flex">
              {g.away_team.logo && (
                <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-sm bg-white/90 p-1.5 shadow-lift">
                  <img src={logo(g.away_team.logo, 80)} alt="" fetchPriority="high" decoding="async" className="h-full w-full object-contain" />
                </span>
              )}
              <p className="h1 min-w-0 leading-[0.88]">
                {g.away_team.abbreviation}
                {started ? <span className="score ml-3 opacity-80">{g.away_team.score ?? 0}</span> : null}
                <span className="mx-3 font-cond text-[30px] font-semibold tracking-normal text-paper/70">at</span>
                {g.home_team.abbreviation}
                {started ? <span className="score ml-3 opacity-80">{g.home_team.score ?? 0}</span> : null}
              </p>
              {g.home_team.logo && (
                <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-sm bg-white/90 p-1.5 shadow-lift">
                  <img src={logo(g.home_team.logo, 80)} alt="" fetchPriority="high" decoding="async" className="h-full w-full object-contain" />
                </span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
              <p className="meta min-w-0 flex-1 truncate !text-paper/85">
                {g.away_team.name} at {g.home_team.name}
              </p>
              {g.odds && (
                <span className="num shrink-0 whitespace-nowrap rounded-sm bg-paper/20 px-2 py-0.5 text-[12px] text-paper">
                  {g.odds}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/game/${g.id}?tab=${isFinal(g) ? 'grades' : 'predict'}`} className="btn-on-blue">
              {isFinal(g) ? 'Coach grades' : 'Predict this game'}
            </Link>
            <Link to={`/game/${g.id}?tab=${isFinal(g) ? 'predict' : 'betting'}`} className="btn border border-paper/50 text-paper hover:bg-paper/15">
              {isFinal(g) ? 'Breakdown' : 'Find the edge'}
            </Link>
          </div>
        </div>
      </div>
    </PageBanner>
  );
}
